import { useRef } from "react";
import {
  IconNameEnum,
  useModuleBEM,
  useUiStyles,
} from "@b2bportal/core-themes";
import { CoreUiComponents } from "@b2bportal/core-types";
import {
  Icon,
  Select,
  SelectItem,
  type SelectComponentProps,
} from "@tenant/ui";
import { useTimePicker } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./TimePicker.module.scss";

interface TimePickerProps
  extends Omit<SelectComponentProps, "value" | "defaultValue" | "onChange"> {
  value?: Date;
  onChange: (value: Date) => void;
  defaultValue?: Date;
  label?: string;
  // interval in minutes
  steps?: number;
  disablePortal?: boolean;
}

/**
 * Display and modify the hour and minute of a Date object
 */

const COMPONENT_KEY = CoreUiComponents.TimePicker;

export function TimePicker({
  steps = 60,
  defaultValue,
  value = defaultValue,
  onChange,
  label,
  className,
  disablePortal = false,
  ...rest
}: TimePickerProps) {
  const selectRef = useRef(null);
  const { options, selectedOption, onSelectChange } = useTimePicker({
    steps,
    value,
    onChange,
  });

  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  return (
    <div className={clsx(block, className)}>
      <Select
        label={label}
        ref={selectRef}
        className={cn("time-select")}
        onValueChange={onSelectChange}
        value={selectedOption}
        startIcon={<Icon iconName={IconNameEnum.clock} />}
        disablePortal={disablePortal}
        {...rest}
      >
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </Select>
    </div>
  );
}
